import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  ArrowLeft,
  Navigation,
  MessageCircle,
  Calendar,
  Users,
  Dumbbell,
  ChevronLeft,
  ChevronRight,
  Send,
  X,
  Upload,
  Loader2,
  Camera,
  Trash2,
} from "lucide-react";
import { API } from "@/App";
import ReviewsSection from "@/components/ReviewsSection";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function BusinessProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [business, setBusiness] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showImageEditModal, setShowImageEditModal] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerToMessage, setTrainerToMessage] = useState(null);
  const [trainerMessage, setTrainerMessage] = useState("");
  const [sendingTrainerMessage, setSendingTrainerMessage] = useState(false);

  useEffect(() => {
    fetchBusinessData();
    fetchCurrentUser();
  }, [id]);
  
  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const user = await response.json();
        setCurrentUserId(user.user_id);
      }
    } catch (error) {
      // User not logged in - that's ok for public page
    }
  };

  // Check if current user is owner of this business
  useEffect(() => {
    if (currentUserId && business) {
      setIsOwner(business.owner_id === currentUserId);
    }
  }, [currentUserId, business]);

  // Image upload handlers
  const resizeImage = async (file, maxWidth, maxHeight, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // File size limits
  const MAX_FILE_SIZE_MB = 10; // 10 MB before resize
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // File type check
    if (!file.type.startsWith('image/')) {
      toast.error(t("common:toast_lutfenBirGorselDosyasiSecin_60a6"));
      return;
    }
    
    // File size check (before resize)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Dosya boyutu çok büyük. Maksimum ${MAX_FILE_SIZE_MB} MB yükleyebilirsiniz.`);
      return;
    }
    
    setUploadingLogo(true);
    try {
      // Resize image (400x400, quality 85%)
      const resizedFile = await resizeImage(file, 400, 400, 0.85);
      const formData = new FormData();
      formData.append('file', resizedFile);
      
      const response = await fetch(`${API}/upload/public-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        const logoUrl = data.url;
        
        // Save to business
        const saveResponse = await fetch(`${API}/businesses/${id}/logo`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ logo: logoUrl }),
        });
        
        if (saveResponse.ok) {
          setBusiness(prev => ({ ...prev, logo: logoUrl }));
          toast.success(t("common:toast_logoYuklendi"));
        } else {
          const errData = await saveResponse.json();
          toast.error(errData.detail || "Logo kaydedilemedi");
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || t("common:toast_gorselYuklenemedi"));
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(t("common:toast_logoYuklenirkenHataOlustu"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // File type check
    if (!file.type.startsWith('image/')) {
      toast.error(t("common:toast_lutfenBirGorselDosyasiSecin_60a6"));
      return;
    }
    
    // File size check (before resize)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Dosya boyutu çok büyük. Maksimum ${MAX_FILE_SIZE_MB} MB yükleyebilirsiniz.`);
      return;
    }
    
    const currentImages = business?.images || [];
    if (currentImages.length >= 8) {
      toast.error(t("common:toast_maksimum8GorselEkleyebilirsiniz"));
      return;
    }
    
    setUploadingImage(true);
    try {
      // Resize image (1200x800, quality 85%)
      const resizedFile = await resizeImage(file, 1200, 800, 0.85);
      const formData = new FormData();
      formData.append('file', resizedFile);
      
      const response = await fetch(`${API}/upload/public-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.url;
        
        const newImages = [...currentImages, imageUrl];
        
        const saveResponse = await fetch(`${API}/businesses/${id}/images`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ images: newImages }),
        });
        
        if (saveResponse.ok) {
          setBusiness(prev => ({ ...prev, images: newImages }));
          toast.success(t("common:toast_gorselEklendi"));
        } else {
          const errData = await saveResponse.json();
          toast.error(errData.detail || t("common:imageSaveFailed"));
        }
      } else {
        toast.error(t("common:toast_gorselYuklenemedi"));
      }
    } catch (error) {
      toast.error(t('common:imageUploadError'));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = async (index) => {
    const newImages = (business?.images || []).filter((_, i) => i !== index);
    try {
      await fetch(`${API}/businesses/${id}/images`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ images: newImages }),
      });
      setBusiness(prev => ({ ...prev, images: newImages }));
      toast.success(t('common:imageRemoved'));
    } catch (error) {
      toast.error(t("common:toast_gorselKaldirilirkenHataOlustu"));
    }
  };

  const removeLogo = async () => {
    try {
      await fetch(`${API}/businesses/${id}/logo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ logo: "" }),
      });
      setBusiness(prev => ({ ...prev, logo: "" }));
      toast.success(t("common:toast_logoKaldirildi"));
    } catch (error) {
      toast.error(t("common:toast_logoKaldirilirkenHataOlustu"));
    }
  };

  const handleSendTrainerMessage = async () => {
    if (!trainerMessage.trim() || !trainerToMessage) return;
    
    setSendingTrainerMessage(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: trainerToMessage.user_id,
          content: trainerMessage.trim(),
          receiver_type: "trainer",
        }),
      });

      if (response.ok) {
        toast.success(t("common:toast_mesajinizGonderildi"));
        setTrainerMessage("");
        setTrainerToMessage(null);
      } else {
        const data = await response.json();
        toast.error(data.detail || t("common:messageSendFailed"));
      }
    } catch (error) {
      toast.error(t("common:toast_mesajGonderilirkenHataOlustu"));
    } finally {
      setSendingTrainerMessage(false);
    }
  };

  const fetchBusinessData = async () => {
    try {
      const [businessRes, trainersRes, ratingsRes] = await Promise.all([
        fetch(`${API}/businesses/${id}`),
        fetch(`${API}/businesses/${id}/trainers`),
        fetch(`${API}/ratings/business/${id}`),
      ]);

      if (businessRes.ok) {
        setBusiness(await businessRes.json());
      }
      if (trainersRes.ok) {
        setTrainers(await trainersRes.json());
      }
      if (ratingsRes.ok) {
        setRatings(await ratingsRes.json());
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
      toast.error(t("common:toast_i̇sletmeBilgileriYuklenemedi"));
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`;
    window.open(url, "_blank");
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    setSendingMessage(true);

    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: business.owner_id,
          content: message,
        }),
      });

      if (response.ok) {
        toast.success(t("common:toast_mesajinizGonderildi_dd37"));
        setMessage("");
        setShowMessageModal(false);
      } else if (response.status === 401) {
        toast.error(t('messaging:loginToSendMessage'));
        navigate("/auth");
      } else {
        throw new Error(t("common:messageSendFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSendingMessage(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? "text-yellow-500 fill-yellow-500" : "text-grey-300"
        }`}
      />
    ));
  };

  const formatWorkingHours = (hours) => {
    if (!hours) return [];
    const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
    return days.map((day, index) => {
      const dayKey = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"][index];
      const dayHours = hours[dayKey];
      return {
        day,
        hours: dayHours ? `${dayHours.open} - ${dayHours.close}` : t("business:closed"),
        isOpen: !!dayHours,
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-navy mb-2">{t("common:i̇sletmeBulunamadi")}</h2>
          <Button onClick={() => navigate("/")} className="mt-4">
            Ana Sayfaya Dön
          </Button>
        </div>
      </div>
    );
  }

  const images = business.images?.length > 0 
    ? business.images 
    : ["https://images.pexels.com/photos/5412246/pexels-photo-5412246.jpeg"];
  
  // Check if current gallery image is a logo (for proper display)
  const currentImage = images[activeImageIndex];
  const isLogoImage = business.logo && (currentImage === business.logo || currentImage.includes('logo'));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-grey hover:text-navy transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{t('common:back')}</span>
          </button>
          <h1 className="font-heading font-bold text-lg text-navy truncate max-w-xs">
            {business.name}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={() => setShowMessageModal(true)}
              data-testid="message-btn"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              className="bg-navy hover:bg-navy/90 rounded-xl"
              onClick={handleNavigate}
              data-testid="navigate-btn"
            >
              <Navigation className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Yol Tarifi</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Image Gallery */}
      <div className={`relative h-64 md:h-96 ${isLogoImage ? 'bg-gradient-to-br from-slate-100 to-slate-200' : 'bg-grey-200'}`}>
        <img
          src={currentImage}
          alt={business.name}
          className={`w-full h-full ${isLogoImage ? 'object-contain p-8' : 'object-cover'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Edit Button for Owner */}
        {isOwner && (
          <button
            onClick={() => setShowImageEditModal(true)}
            className="absolute top-4 right-4 bg-white/90 hover:bg-white text-navy px-3 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
          >
            <Camera className="w-4 h-4" />
            <span className="text-sm font-medium">{t("common:gorselleriDuzenle")}</span>
          </button>
        )}
        
        {/* Image Navigation */}
        {images.length > 1 && (
          <>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              onClick={() => setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
            >
              <ChevronLeft className="w-5 h-5 text-navy" />
            </button>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              onClick={() => setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
            >
              <ChevronRight className="w-5 h-5 text-navy" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === activeImageIndex ? "bg-white w-6" : "bg-white/50"
                  }`}
                  onClick={() => setActiveImageIndex(index)}
                />
              ))}
            </div>
          </>
        )}

        {/* Business Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="container mx-auto">
            <Badge className="mb-2 bg-white/20 text-white border-0">
              {business.business_type === "corporate" ? "Kurumsal" : "Bireysel"}
            </Badge>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">
              {business.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{business.city}, {business.district}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="font-semibold">{business.rating?.toFixed(1) || "5.0"}</span>
                <span className="text-white/70">({business.rating_count || 0} değerlendirme)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="w-full justify-start rounded-xl bg-white p-1 shadow-sm">
                <TabsTrigger value="about" className="rounded-lg">{t('business:about')}</TabsTrigger>
                {/* Antrenörler tab - only show if display_settings.show_trainers_publicly is not false */}
                {business.display_settings?.show_trainers_publicly !== false && (
                  <TabsTrigger value="trainers" className="rounded-lg">{t('common:trainers')}</TabsTrigger>
                )}
                <TabsTrigger value="reviews" className="rounded-lg">Yorumlar</TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="mt-6">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-6">
                    {/* Branches */}
                    <div className="mb-6">
                      <h3 className="font-heading font-semibold text-navy mb-3">{t('common:branches')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {business.branches?.map((branch) => (
                          <Badge key={branch} variant="secondary" className="badge-branch">
                            {getLocalizedBranch(branch)}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Training Types */}
                    <div className="mb-6">
                      <h3 className="font-heading font-semibold text-navy mb-3">Antrenman Tipleri</h3>
                      <div className="flex flex-wrap gap-2">
                        {business.training_types?.map((type, index) => {
                          // Handle both legacy codes and actual names
                          const displayName = type === "one_on_one" ? "Birebir" 
                            : type === "duet" ? "Düet" 
                            : type === "group" ? "Grup" 
                            : type; // Use actual name if not a legacy code
                          return (
                            <Badge key={`${type}-${index}`} variant="outline" className="rounded-full">
                              {displayName}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    {/* Working Hours */}
                    <div className="mb-6">
                      <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Çalışma Saatleri
                      </h3>
                      <div className="space-y-2">
                        {formatWorkingHours(business.working_hours).map((item) => (
                          <div
                            key={item.day}
                            className={`flex justify-between py-2 px-3 rounded-lg ${
                              item.isOpen ? "bg-green-50" : "bg-grey-50"
                            }`}
                          >
                            <span className="font-medium">{item.day}</span>
                            <span className={item.isOpen ? "text-green-600" : "text-grey"}>
                              {item.hours}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Accepted Cards */}
                    {business.accepted_cards?.length > 0 && (
                      <div>
                        <h3 className="font-heading font-semibold text-navy mb-3">
                          Geçerli Kartlar
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {business.accepted_cards.map((card) => (
                            <Badge key={card} variant="secondary" className="rounded-full">
                              {card}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Trainers Tab Content - only show if display_settings.show_trainers_publicly is not false */}
              {business.display_settings?.show_trainers_publicly !== false && (
              <TabsContent value="trainers" className="mt-6">
                <div className="space-y-4">
                  {trainers.length > 0 ? (
                    trainers.map((trainer) => (
                      <Card 
                        key={trainer.trainer_id} 
                        className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => setSelectedTrainer(trainer)}
                      >
                        <CardContent className="p-4 flex items-center gap-4">
                          <Avatar className="h-16 w-16">
                            <AvatarFallback className="bg-red-500 text-white text-lg">
                              {trainer.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h4 className="font-heading font-semibold text-navy">
                              {trainer.name} {trainer.surname}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex">{renderStars(Math.round(trainer.rating || 5))}</div>
                              <span className="text-sm text-grey">({trainer.rating_count || 0})</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {trainer.branches?.map((branch) => (
                                <Badge key={branch} variant="secondary" className="text-xs">
                                  {getLocalizedBranch(branch)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                        size="sm"
                        className="bg-red-500 hover:bg-red-600 text-white rounded-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTrainerToMessage(trainer);
                        }}
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        Mesaj Gönder
                      </Button>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Dumbbell className="w-12 h-12 text-grey mx-auto mb-3" />
                      <p className="text-grey">{t("common:henuzAntrenorEklenmemis")}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              )}

              <TabsContent value="reviews" className="mt-6">
                <ReviewsSection
                  targetType="business"
                  targetId={id}
                  targetName={business?.name}
                  currentUserId={currentUserId}
                  showCreateButton={true}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="border-0 shadow-sm sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{t('business:contact')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <p className="text-sm text-grey">{t('common:phone')}</p>
                    <a
                      href={`tel:${business.phone}`}
                      className="font-medium text-navy hover:underline"
                    >
                      {business.phone}
                    </a>
                  </div>
                </div>

                {business.email && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center">
                      <Mail className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <p className="text-sm text-grey">{t('common:email')}</p>
                      <a
                        href={`mailto:${business.email}`}
                        className="font-medium text-navy hover:underline"
                      >
                        {business.email}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <p className="text-sm text-grey">{t('common:address')}</p>
                    <p className="font-medium text-navy">{business.address}</p>
                    <p className="text-sm text-grey">{business.city}, {business.district}</p>
                  </div>
                </div>

                <div className="pt-4 space-y-3">
                  <Button
                    className="w-full bg-navy hover:bg-navy/90 rounded-xl h-12"
                    onClick={() => setShowMessageModal(true)}
                    data-testid="send-message-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Mesaj Gönder
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full rounded-xl h-12"
                    onClick={handleNavigate}
                  >
                    <Navigation className="w-5 h-5 mr-2" />
                    Yol Tarifi Al
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMessageModal(false)}
          />
          <Card className="relative w-full max-w-md border-0 shadow-2xl animate-fade-in">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t('messaging:sendMessage')}</CardTitle>
              <button
                onClick={() => setShowMessageModal(false)}
                className="p-1 hover:bg-grey-100 rounded-lg"
              >
                <X className="w-5 h-5 text-grey" />
              </button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-grey mb-4">
                <strong>{business.name}</strong> işletmesine mesaj gönderin
              </p>
              <Textarea
                placeholder={t('messaging:typeMessage')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-32 rounded-xl mb-4"
                data-testid="message-textarea"
              />
              <Button
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleSendMessage}
                disabled={sendingMessage || !message.trim()}
                data-testid="send-message-submit"
              >
                {sendingMessage ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Gönder
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Image Edit Modal */}
      {showImageEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t("common:gorselleriDuzenle")}</CardTitle>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setShowImageEditModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Section */}
              <div>
                <h3 className="font-medium mb-3">{t("common:i̇sletmeLogosu")}</h3>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-grey-300 flex items-center justify-center overflow-hidden bg-grey-50">
                    {business.logo ? (
                      <img src={business.logo} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-grey-400" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors text-sm">
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Yükleniyor...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Logo Yükle
                          </>
                        )}
                      </span>
                    </label>
                    {business.logo && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={removeLogo}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Kaldır
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trainer Detail Modal */}
      {selectedTrainer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedTrainer(null)}
          />
          <Card className="relative w-full max-w-md border-0 shadow-2xl animate-fade-in">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading">{t("trainers:trainerInfo")}</CardTitle>
              <button
                onClick={() => setSelectedTrainer(null)}
                className="p-1 hover:bg-grey-100 rounded-lg"
              >
                <X className="w-5 h-5 text-grey" />
              </button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="bg-red-500 text-white text-2xl">
                    {selectedTrainer.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-heading font-bold text-xl text-navy">
                    {selectedTrainer.name} {selectedTrainer.surname}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex">{renderStars(Math.round(selectedTrainer.rating || 5))}</div>
                    <span className="text-sm text-grey">({selectedTrainer.rating_count || 0} değerlendirme)</span>
                  </div>
                </div>
              </div>

              {/* Branches */}
              {selectedTrainer.branches?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-navy mb-2">{t('common:branches')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTrainer.branches.map((branch) => (
                      <Badge key={branch} variant="secondary" className="rounded-full">
                        {getLocalizedBranch(branch)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {selectedTrainer.experience_years && (
                <div className="mb-4">
                  <h4 className="font-medium text-navy mb-2">Deneyim</h4>
                  <p className="text-grey">{selectedTrainer.experience_years} yıl deneyim</p>
                </div>
              )}

              {/* About */}
              {selectedTrainer.about && (
                <div className="mb-4">
                  <h4 className="font-medium text-navy mb-2">{t('business:about')}</h4>
                  <p className="text-grey text-sm">{selectedTrainer.about}</p>
                </div>
              )}

              <Button
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-xl mt-4"
                onClick={() => {
                  setSelectedTrainer(null);
                  setTrainerToMessage(selectedTrainer);
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Mesaj Gönder
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trainer Message Modal */}
      {trainerToMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setTrainerToMessage(null)}
          />
          <Card className="relative w-full max-w-md border-0 shadow-2xl animate-fade-in">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t("common:antrenoreMesaj")}</CardTitle>
              <button
                onClick={() => setTrainerToMessage(null)}
                className="p-1 hover:bg-grey-100 rounded-lg"
              >
                <X className="w-5 h-5 text-grey" />
              </button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-xl">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-red-500 text-white">
                    {trainerToMessage.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-navy">
                    {trainerToMessage.name} {trainerToMessage.surname}
                  </p>
                  <p className="text-sm text-grey">{t('common:trainer')}</p>
                </div>
              </div>
              <Textarea
                placeholder={t('messaging:typeMessage')}
                value={trainerMessage}
                onChange={(e) => setTrainerMessage(e.target.value)}
                className="min-h-32 rounded-xl mb-4"
              />
              <Button
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-xl"
                onClick={handleSendTrainerMessage}
                disabled={sendingTrainerMessage || !trainerMessage.trim()}
              >
                {sendingTrainerMessage ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Gönder
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
